import type { Dispatch } from 'redux';
import type {
    TranscriptData as LuminaryTranscriptData,
    TranscriptWordData,
} from '@lumen5/luminary';
import { createHeadlessEditor } from '@lexical/headless';
import { $dfs, $getNearestNodeOfType } from '@lexical/utils';
import type { EditorState, LexicalEditor, SerializedEditorState } from 'lexical';
import { $createLineBreakNode, $createParagraphNode, $getRoot, $nodesOfType } from 'lexical';

import type {
    AudioAssetModel,
    AudioAssetSourceData,
    EditedTranscriptWord,
    EditedTranscriptWordRequest,
    LanguageLocalesEnum,
    TranscriptWord,
} from 'js/src/client';
import {
    ApiService,
    AsyncOperationNameEnum,
    LanguageEnum,
    TaskResultMessageTypeEnum,
    TextDirectionEnum,
} from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    getVideoId,
    markSceneGroupAsFinishedLoading,
    markSceneGroupAsLoading,
    SCENE_GROUP_LOADING_TYPES,
} from 'js/src/data/app';
import StoreORM from 'js/src/data/store-orm';
import type {
    InterestPointTime,
    LexicalEditorsDataType,
    SerializedTranscript,
    TranscriptData,
    TranscriptTranslations,
} from 'js/src/data/transcript';
import {
    disableTranscriptOverlays,
    endLexicalControllingItsOwnState,
    endTimingMode,
    ENTER_TIMING_MODE_COMMAND,
    EXIT_TIMING_MODE_COMMAND,
    getEditorFromSceneGroupId,
    getLexicalEditors,
    getOriginalTranscriptLanguage,
    getTimingModeSceneGroupId,
    getTranscriptById,
    getTranscripts,
    getTranscriptTranslationLanguage,
    getTranscriptWords,
    InterestPointTimeType,
    isTimingModeActive,
    overwriteAllLexicalEditors,
    setSceneConversionModalVisible,
    setTranscriptCurrentTime,
    setTranscriptLanguage,
    setTranscriptTabVisibility,
    startLexicalControllingItsOwnState,
    startTimingMode,
    storeLexicalEditor,
    storeRawTranscript,
    storeTranscriptTranslations,
    TypeOfTranscript,
    VALIDATE_TRANSCRIPT_COMMAND,
} from 'js/src/data/transcript';
import { BaseOverlayElementNode } from 'js/src/data/transcript/nodes/BaseOverlayElementNode';
import { OverlayElementNode, OverlayState } from 'js/src/data/transcript/nodes/OverlayElementNode';
import {
    $createSilenceNode,
    $isSilenceNode,
    SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT,
    SilenceNode,
} from 'js/src/data/transcript/nodes/SilenceNode';
import {
    $createTranscriptWordNode,
    $isTranscriptWordNode,
    TranscriptWordNode,
} from 'js/src/data/transcript/nodes/TranscriptWordNode';
import { $isTrimElementNode } from 'js/src/data/transcript/nodes/TrimElementNode';
import {
    $findMatchWithin,
    $getFineOverlayBoundaryTimeCloseTo,
    $getFirstLastWordsBySceneId,
    $getLastNonEmptyWordWithin,
    $getLastTranscriptWordShowing,
    $getNextTranscriptWordNode,
    $getNextWordAtTime,
    $getNodeTimeBounds,
    $getOverlaySceneIdImmediatelyAfter,
    $getOverlaySceneIdImmediatelyBefore,
    $getPreviousTranscriptWordNode,
    $getPreviousWordAtTime,
    $getSelectedNodes,
    $getWordNodeByWordId,
    $isSpace,
    $isTrimmedOut,
    createLexicalEditor,
    getFullTranscriptFromEditor,
    getNextTranslationEditorName,
    getSilenceNodeTimes,
    getTranscriptDuration,
    theseAreOverlapping,
    thisIsWithinRange,
    TRANSCRIPT_EDITOR_CONFIG,
    updateEditor,
} from 'js/src/data/transcript/transcript-utils';
import {
    getTranslatedEditorBySceneGroupId,
    removeTranslatedEditor,
    storeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors';
import { reduceTranscriptsToTranslationEditorsMap } from 'js/src/data/translated-editors/translated-editors.utils';
import {
    getLanguageTextDirection,
    getSupportedLanguages,
    shouldSeparateTranscriptWordsWithSpaces,
    shouldSeparateWordsWithSpaces,
    translateText,
} from 'js/src/data/translations';
import { convertLocaleToLanguageCode } from 'js/src/data/translations/utils';
import { getAssetsWithTimesOverridden } from 'js/src/data/user-overrides/user-overrides-selectors';
import {
    checkpointVideo,
    resetLuminaryAndLexicalToPresentCheckpointOperation,
} from 'js/src/data/video-data';
import { BatchAnalytics } from 'js/src/libs/BatchAnalytics';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, LOADING_SCENE_MSG, TRANSCRIPT_CONFIG } from 'js/src/libs/consts';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { StartScenePreviewPlaybackPayload } from 'js/src/libs/slide-event-emitter';
import SlideEventEmitter, { SLIDE_EVENTS } from 'js/src/libs/slide-event-emitter';
import uuid from 'js/src/libs/uuid';
import WebSocketClient from 'js/src/libs/websocket-client';
import {
    cutVoicedrivenSceneGroupOperation,
    saveRawLuminary,
    updateSceneGroupDurationToEqualVoiceDrivenAssetDuration,
} from 'js/src/luminary';
import { equalWithinEpsilon } from 'js/src/luminary/comparison-utils';
import { createLuminaryOperation } from 'js/src/luminary/luminary-hooks';
import { setAssetIntroExitTimesOperation } from 'js/src/luminary/luminary-operations';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import type { PlaybackRegions } from 'js/src/luminary/luminary-selectors';
import {
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenAssetSourceData,
    getVoiceDrivenBaseAssetCutRegions,
    getVoiceDrivenBaseAssetEndTime,
    getVoiceDrivenBaseAssetSourceDuration,
    getVoiceDrivenBaseAssetStartTime,
    isAIVoiceoverSceneGroup,
    isVoiceDrivenSceneGroup,
    translateSceneGroupTimeToTranscriptTime,
    translateTranscriptTimeToVideoTime,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';
import {
    setVoiceDrivenBaseAssetEndTime,
    setVoiceDrivenBaseAssetStartTime,
} from 'js/src/luminary/luminary-utils';

export function convertTranscriptWordsToMillis<T extends EditedTranscriptWord | TranscriptWord>(
    transcriptWords: T[],
): T[] {
    return transcriptWords.map((word) => {
        return { ...word, startTime: word.startTime * 1000, endTime: word.endTime * 1000 };
    });
}

export function hydrateTranscripts(
    transcripts: Record<string, SerializedTranscript | SerializedEditorState>,
) {
    return (dispatch: Dispatch) => {
        const serializedTranscripts: Record<string, SerializedTranscript> = {};
        for (const sceneGroupId in transcripts) {
            let editorState: SerializedEditorState = (
                transcripts[sceneGroupId] as SerializedTranscript
            ).editorState;
            if (editorState) {
                const transcript = transcripts[sceneGroupId] as SerializedTranscript;
                const disableOverlays = transcript.disableOverlays === true;
                const translations = ImmutableFromJS(transcript.translations);
                const language = transcript.language;
                dispatch(
                    storeRawTranscript({
                        sceneGroupId,
                        editorState,
                        isOriginal: false,
                        language,
                        systemTriggered: true,
                        parentActionGroup: undefined,
                    }),
                );
                disableOverlays &&
                    dispatch(
                        disableTranscriptOverlays({
                            sceneGroupId,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );

                if (translations) {
                    dispatch(
                        storeTranscriptTranslations({
                            sceneGroupId,
                            translations,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );
                }

                serializedTranscripts[sceneGroupId] = transcripts[
                    sceneGroupId
                ] as SerializedTranscript;
            } else {
                editorState = transcripts[sceneGroupId] as SerializedEditorState;
                dispatch(
                    storeRawTranscript({
                        sceneGroupId,
                        editorState,
                        isOriginal: false,
                        systemTriggered: true,
                        parentActionGroup: undefined,
                    }),
                );
            }
        }

        // store translations, work with only SerializedTranscript types because we need transcript[sceneGroupId].translations
        if (Object.keys(serializedTranscripts).length > 0) {
            const translationEditors =
                reduceTranscriptsToTranslationEditorsMap(serializedTranscripts);
            translationEditors.forEach((translationEditor) =>
                dispatch(storeTranslatedEditors(translationEditor)),
            );
        }
    };
}

export function hydrateLexicalEditorsFromRedux() {
    return (dispatch: Dispatch, getState: GetRootState) => {
        const state = getState();
        const transcriptDataOrm = new StoreORM<TranscriptData, string>(getTranscripts(state));
        const editors: LexicalEditorsDataType[] = [];
        transcriptDataOrm.forEach((transcriptData) => {
            editors.push({
                editor: createEditorFromEditorState(transcriptData.get('editorState')),
                sceneGroupId: transcriptData.get('sceneGroupId'),
                lexicalControlsItsOwnState: false,
            });
        });
        dispatch(overwriteAllLexicalEditors(editors));
    };
}

export function createEditorFromEditorState(
    editorState: SerializedEditorState | ImmutableMap<SerializedEditorState>,
) {
    const editor = createLexicalEditor(TRANSCRIPT_EDITOR_CONFIG);
    const parsedEditorState = editor.parseEditorState(JSON.stringify(editorState));
    // Editor state doesn't get set if it's not in headless mode.
    // This isn't ideal but it's at least an ok temporary solution.
    editor._headless = true;
    editor.setEditorState(parsedEditorState);
    editor._headless = false;
    return editor;
}

type CheckpointVideoAndTranscriptOptions = {
    isOriginalTranscript?: boolean;
    waitForLuminary?: boolean;
};

export const checkpointVideoAndAllTranscriptsOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        options: CheckpointVideoAndTranscriptOptions = {
            isOriginalTranscript: false,
            waitForLuminary: false,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        const orm = new StoreORM<LexicalEditorsDataType, string>(getLexicalEditors(getState()));
        orm.forEach((editorData) => {
            call(
                saveTranscriptOperation,
                editorData.get('sceneGroupId'),
                editorData.get('editor') as unknown as LexicalEditor, // ImmutableJS confuses typescript
                {
                    isOriginal: options.isOriginalTranscript,
                    waitForLuminary: options.waitForLuminary,
                },
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

export const checkpointVideoAndTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        options: CheckpointVideoAndTranscriptOptions = {
            isOriginalTranscript: false,
            waitForLuminary: false,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        call(
            saveTranscriptOperation,
            sceneGroupId,
            editor,
            { isOriginal: options.isOriginalTranscript, waitForLuminary: options.waitForLuminary },
            systemTriggered,
            parentActionGroup,
        );
    },
);

type SaveTranscriptOptions = {
    isOriginal?: boolean;
    language?: LanguageEnum;
    waitForLuminary?: boolean;
};
export const saveTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState },
        sceneGroupId: string,
        editor: LexicalEditor,
        options: SaveTranscriptOptions = {
            isOriginal: false,
            language: undefined,
            waitForLuminary: true,
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        /*
        editor.dispatchCommand dispatches a command, allowing potentially multiple listeners to
        react to the command using editor.update(callback). At the end of the editor.update()
        function, lexical enqueues a microtask to actually commit the updates:
        https://github.com/facebook/lexical/blob/5bfb7300e23614a8ca1ddd747352e8e057143cc2/packages/lexical/src/LexicalUpdates.ts#L953-L955

        We want to make sure that the transcript that we are saving to redux is the most up-to-date one (after
        applying any potential changes from the command listeners).

        So we also need to use queueMicrotask here!
        */
        queueMicrotask(async () => {
            /*
            The idea with the transcript validation plugin is that we can:

            - make changes to luminary
            - make changes to the editor using `useTranscriptOperation`
            - validate + save the transcript

            As long as that order is followed, and the changes are synchronous, everything works well.
            However, sometimes our code needs to load something in luminary before the changes can be finalized.

            For example, we might:

            - make changes to luminary
            - make changes to the editor using `useTranscriptOperation`
            - await luminary.onCanPlay() -> read some luminary state, then make more changes to luminary
            - validate + save the transcript

            In this case, we also need the editor validation + saving code to wait for luminary to complete it's update.
            That's why we put `await luminary.onCanPlay()` here.
            */
            if (options.waitForLuminary) {
                await luminary.onCanPlay();
            }

            editor.dispatchCommand(VALIDATE_TRANSCRIPT_COMMAND, undefined);
            const editorState = editor.getEditorState();
            dispatch(
                saveTranscript(
                    sceneGroupId,
                    editorState,
                    !!options.isOriginal,
                    options.language,
                    systemTriggered,
                    parentActionGroup,
                ),
            );
        });
    },
);

export function saveTranscript(
    sceneGroupId: string,
    editorState: EditorState,
    isOriginal: boolean,
    language: LanguageEnum | undefined = undefined,
    systemTriggered: boolean = false,
    parentActionGroup: string = uuid(),
) {
    return (dispatch: AppDispatch) => {
        if (!editorState.isEmpty()) {
            dispatch(
                storeRawTranscript({
                    sceneGroupId,
                    editorState: editorState.toJSON(),
                    isOriginal,
                    language,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        }
    };
}

export enum SilenceNodeGenerationMethod {
    DoNotGenerate = 'DoNotGenerate',
    GenerateFromTranscriptWord = 'GenerateFromTranscriptWord',
    GenerateFromWordGap = 'GenerateFromWordGap',
}

/**
 * Generates Lexical word nodes, line breaks, and silence nodes from transcript words.
 *
 * @param durationMillis : Only applicable when silenceNodeGenerationMethod is GenerateFromWordGap
 */
const $generateEditorFromTranscript = (
    transcriptWords: EditedTranscriptWord[] | TranscriptWord[],
    langUsesSpaces: boolean,
    silenceNodeGenerationMethod: SilenceNodeGenerationMethod,
    durationMillis?: number,
) => {
    const root = $getRoot();
    root.clear();
    const paragraphNode = $createParagraphNode();
    let prevWordEndTime = 0;
    const createSilenceNode = (startTime: number, endTime: number, id?: string) => {
        const silenceNode = $createSilenceNode(startTime, endTime, { id });
        paragraphNode.append(silenceNode);
    };

    transcriptWords.forEach((entry: EditedTranscriptWord | TranscriptWord) => {
        const wordStartMillis = entry.startTime;
        const wordEndMillis = entry.endTime;

        if (silenceNodeGenerationMethod === SilenceNodeGenerationMethod.GenerateFromWordGap) {
            const silenceNodeTimes = getSilenceNodeTimes(prevWordEndTime, wordStartMillis);
            silenceNodeTimes.forEach((entry) => {
                const { startTime, endTime } = entry;
                createSilenceNode(startTime, endTime);
            });
        }

        // Create node from the transcript word
        const id = Object.hasOwnProperty.call(entry, 'id')
            ? (entry as EditedTranscriptWord).id
            : uuid();
        if (
            silenceNodeGenerationMethod ===
                SilenceNodeGenerationMethod.GenerateFromTranscriptWord &&
            entry.word === SILENCE_TRANSCRIPT_WORD_TEXT_CONTENT
        ) {
            createSilenceNode(entry.startTime, entry.endTime, id);
        } else {
            const textNode = $createTranscriptWordNode({
                text: entry.word,
                startTime: entry.startTime,
                endTime: entry.endTime,
                isFiller: entry.filler,
                id,
            });
            paragraphNode.append(textNode);
            if (langUsesSpaces) {
                const spaceNode = $createTranscriptWordNode({
                    text: ' ',
                    startTime: entry.endTime,
                    endTime: entry.endTime,
                    isFiller: false, // spaces are not filler words
                });
                paragraphNode.append(spaceNode);
            }
        }

        prevWordEndTime = wordEndMillis;
    });
    if (
        silenceNodeGenerationMethod === SilenceNodeGenerationMethod.GenerateFromWordGap &&
        durationMillis
    ) {
        const silenceNodeTimes = getSilenceNodeTimes(prevWordEndTime, durationMillis);
        silenceNodeTimes.forEach((entry) => {
            const { startTime, endTime } = entry;
            createSilenceNode(startTime, endTime);
        });
    }

    // Create line breaks nodes
    const wordNodes = paragraphNode.getChildren().filter($isTranscriptWordNode);
    if (wordNodes.length !== paragraphNode.getChildrenSize()) {
        throw new Error('All paragraph node children should be transcript word nodes');
    }
    let currentSentenceCount = 0;
    let currentSilenceCount = 0;
    let index = 0;
    let prevWordLastChar = '';
    let prevTextWordEndTime = 0;
    while (index < wordNodes.length) {
        let node = wordNodes[index];
        if ($isSpace(node)) {
            index++;
            continue;
        }
        let nextWordIndex = index;
        while ($isSilenceNode(node) && nextWordIndex < wordNodes.length) {
            // Keep grabbing the next node until we reach a text word node or the last node
            nextWordIndex++;
            node = wordNodes[nextWordIndex];
            currentSilenceCount++;
        }
        if (!node) {
            // We reached the end, do not create any more line breaks
            break;
        }
        const wordStartMillis = node.startTime;
        const wordGapMillis = wordStartMillis - prevTextWordEndTime;
        const wordGapOverThreshold =
            wordGapMillis >= TRANSCRIPT_CONFIG.LINEBREAK_PAUSE_THRESHOLD_SECONDS * 1000;
        const shouldCreateLineBreak =
            prevTextWordEndTime &&
            (wordGapOverThreshold ||
                currentSentenceCount >= TRANSCRIPT_CONFIG.MAX_PARAGRAPH_SENTENCE_COUNT) &&
            ['.', '?'].includes(prevWordLastChar);

        if (shouldCreateLineBreak) {
            // Create line break in the middle of the silence nodes. For odd numer of silence nodes, the extra silence node should be after the line break
            const indexToInsert = Math.floor(currentSilenceCount / 2) + index - 1;
            wordNodes[indexToInsert].insertAfter($createLineBreakNode());
            currentSentenceCount = 0;
        }

        // Update variables and continue to the next word
        const prevWordText = node.getTextContent();
        prevWordLastChar = prevWordText[prevWordText.length - 1];
        prevTextWordEndTime = node.endTime;
        if (['.', '?'].includes(prevWordLastChar)) {
            currentSentenceCount += 1;
        }
        currentSilenceCount = 0;
        index = nextWordIndex + 1;
    }

    root.append(paragraphNode);
};

export const buildTranslatedTranscript = (
    translatedWordNodes: TranscriptWord[],
    langCode: LanguageEnum,
) => {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        // Create a temporary editor instance using a temporary hidden dom element
        const editor = createLexicalEditor(
            TRANSCRIPT_EDITOR_CONFIG,
            getNextTranslationEditorName(),
        );
        const contentEditableElement = document.createElement('div');
        contentEditableElement.style.display = 'none';
        editor.setRootElement(contentEditableElement);

        const useSpaces = shouldSeparateWordsWithSpaces(getState(), langCode || LanguageEnum.XX);
        await updateEditor(editor, () => {
            $generateEditorFromTranscript(
                translatedWordNodes,
                useSpaces,
                SilenceNodeGenerationMethod.DoNotGenerate,
            );
        });

        return editor;
    };
};

/**
 * This function creates a temporary editor instance using a temporary hidden DOM element.
 * The main outcome is so that we can create and store the initial editor, which will be used throughout
 * the rest of the application.
 */
export const loadNewTranscriptOperation = createLuminaryOperation(
    (
        { luminary, dispatch, getState, call },
        transcriptWords: TranscriptWord[],
        transcriptLanguage: LanguageEnum | undefined,
        sceneGroupId: string,
        sceneId: string,
        showTranscript: boolean,
        isOnboarding: boolean = false,
        creationStartTime: number | null = null,
        isFromScene: boolean = false,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        // Create a temporary editor instance using a temporary hidden dom element
        const contentEditableElement = document.createElement('div');
        const editor = createLexicalEditor(TRANSCRIPT_EDITOR_CONFIG);
        contentEditableElement.style.display = 'none';
        editor.setRootElement(contentEditableElement);

        return new Promise<LexicalEditor>((resolve) => {
            updateEditor(editor, () => {
                const useSpaces = shouldSeparateWordsWithSpaces(
                    getState(),
                    transcriptLanguage || LanguageEnum.XX,
                );
                const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
                const durationMillis = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                const silenceNodeGenerationMethod = isAIVoiceoverSceneGroup(sceneGroup)
                    ? SilenceNodeGenerationMethod.GenerateFromTranscriptWord
                    : SilenceNodeGenerationMethod.GenerateFromWordGap;
                $generateEditorFromTranscript(
                    transcriptWords,
                    useSpaces,
                    silenceNodeGenerationMethod,
                    durationMillis,
                );
            }).then(() => {
                dispatch(
                    saveTranscript(
                        sceneGroupId,
                        editor.getEditorState(),
                        true,
                        transcriptLanguage,
                        systemTriggered,
                        parentActionGroup,
                    ),
                );
                dispatch(
                    storeLexicalEditor({ lexicalControlsItsOwnState: false, sceneGroupId, editor }),
                );
                // Disable overlays for transcripts that are created from scene captions (MR-2572)
                if (isFromScene) {
                    dispatch(
                        disableTranscriptOverlays({
                            sceneGroupId,
                            systemTriggered: true,
                            parentActionGroup: undefined,
                        }),
                    );
                    dispatch(setSceneConversionModalVisible(true));
                }
                showTranscript && dispatch(setTranscriptTabVisibility(true));
                // We have saved the editor state, so we can safely delete the dom element
                contentEditableElement && contentEditableElement.remove();

                analytics.track('Transcript initialized', {
                    sceneGroupId,
                    sceneId,
                    numWords: transcriptWords.length,
                    transcriptDuration:
                        transcriptWords.length > 1
                            ? transcriptWords.at(-1)!.endTime - transcriptWords[0].startTime
                            : 0,
                    isOnboarding,
                    creationTime: creationStartTime ? performance.now() - creationStartTime : null,
                });
                resolve(editor);
            });
        });
    },
);

export function duplicateTranscript(
    sceneGroupId: string,
    sourceEditorState: SerializedEditorState,
    language: LanguageEnum | undefined,
    newSceneIdMap: { [sceneId: string]: string },
    systemTriggered: boolean,
    parentActionGroup: string,
) {
    return (dispatch: AppDispatch) => {
        const editor = createHeadlessEditor(TRANSCRIPT_EDITOR_CONFIG);
        const editorState = editor.parseEditorState(JSON.stringify(sourceEditorState));
        editor.setEditorState(editorState);
        editor.update(
            () => {
                const nodes = $nodesOfType(OverlayElementNode);
                if (nodes.length > 0) {
                    for (const node of nodes) {
                        node.setSceneId(newSceneIdMap[node.sceneId]);
                        node.setState(OverlayState.Default);
                    }
                } else {
                    // onUpdate (below) is only called if an update has been made; in this case, if there is an overlay
                    // so we also need to call saveTranscript here too to handle the scenario where there are no overlays
                    dispatch(
                        saveTranscript(
                            sceneGroupId,
                            editor.getEditorState(),
                            false,
                            language,
                            systemTriggered,
                            parentActionGroup,
                        ),
                    );
                }
            },
            {
                onUpdate: () => {
                    dispatch(
                        saveTranscript(
                            sceneGroupId,
                            editor.getEditorState(),
                            false,
                            language,
                            systemTriggered,
                            parentActionGroup,
                        ),
                    );
                },
            },
        );
    };
}

export function storeTranscriptTranslationsFromEditor(
    editor: LexicalEditor,
    translatedLanguage: string,
    sceneGroupId: string,
    systemTriggered: boolean,
    parentActionGroup: string,
) {
    return (dispatch: AppDispatch) => {
        const editorState = editor.getEditorState();
        if (editorState.isEmpty()) {
            return;
        }

        const translations = {
            [translatedLanguage]: { editorState: editorState.toJSON() },
        } as TranscriptTranslations;
        dispatch(
            storeTranscriptTranslations({
                sceneGroupId,
                translations: ImmutableFromJS(translations),
                systemTriggered,
                parentActionGroup,
            }),
        );
    };
}

/**
 * This updates the current time of the transcript by performing two actions:
 * 1. Updates the redux state for the current time relative to the scene group
 * 2. Emits a slide event to trigger a new preview to be generated and used
 */
export function updateTranscriptCurrentTimeAndPreview(payload: {
    sceneGroupId: string;
    sceneId: string;
    renderPreviewAtTime: number;
    playPreviewFromTime?: number;
}) {
    return (dispatch: AppDispatch) => {
        const transcriptCurrentTime = payload.playPreviewFromTime ?? payload.renderPreviewAtTime;
        dispatch(
            setTranscriptCurrentTime({
                sceneGroupId: payload.sceneGroupId,
                sceneId: payload.sceneId,
                currentTimeMillis: transcriptCurrentTime,
            }),
        );
        SlideEventEmitter.emit(SLIDE_EVENTS.UPDATE_PREVIEW_AT_TIME, {
            sceneGroupId: payload.sceneGroupId,
            timeRelativeToSceneGroup: payload.renderPreviewAtTime,
        });
    };
}

/**
 * This function is useful to find what the expected boundary of an overlay should be (when it should start and end).
 * It takes into account the state of luminary (if there are other overlays that are immediately neighbouring "this" one),
 * as well as the state of the transcript (the word node boundaries).
 * @param startNode the first node in "this" overlay
 * @param endNode the last node in "this overlay"
 * @param sceneGroupId the sceneGroupId for the transcript
 * @returns {{startTime: number, endTime: number}} the proper start and end time of the overlay, in video time
 */
export const getOverlayTimeBoundaryFromLuminaryOperation = createLuminaryOperation(
    (
        { luminary },
        startNode: TranscriptWordNode,
        endNode: TranscriptWordNode,
        sceneGroupId: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        let { startTime, endTime } = $getNodeTimeBounds(
            startNode,
            endNode,
            voiceDrivenAssetPlaybackRegions,
        );
        startTime = translateTranscriptTimeToVideoTime(
            startTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );
        endTime = translateTranscriptTimeToVideoTime(
            endTime,
            voiceDrivenAssetPlaybackRegions,
            voiceDrivenAssetSourceDuration,
            sceneGroup.startTime,
        );

        const startNodeOverlaySceneId = $getNearestNodeOfType(
            startNode,
            OverlayElementNode,
        )?.sceneId;
        const endNodeOverlaySceneId = $getNearestNodeOfType(endNode, OverlayElementNode)?.sceneId;

        const overlaySceneIdRightBefore = $getOverlaySceneIdImmediatelyBefore(startNode);
        const overlaySceneIdRightAfter = $getOverlaySceneIdImmediatelyAfter(endNode);

        if (
            (overlaySceneIdRightBefore || startNodeOverlaySceneId) &&
            overlaySceneIdRightBefore !== startNodeOverlaySceneId
        ) {
            const usingOverlaySceneBefore = !!overlaySceneIdRightBefore;
            const sceneId = usingOverlaySceneBefore
                ? overlaySceneIdRightBefore
                : startNodeOverlaySceneId;
            if (!sceneId) {
                throw new Error('Overlay scene ID not found');
            }
            const overlay = luminary.getSceneById(sceneId);
            if (!overlay) {
                throw new Error('Overlay missing from luminary!');
            }
            startTime = usingOverlaySceneBefore ? overlay.endTime : overlay.startTime;
        }
        if (
            (overlaySceneIdRightAfter || endNodeOverlaySceneId) &&
            overlaySceneIdRightAfter !== endNodeOverlaySceneId
        ) {
            const usingOverlaySceneAfter = !!overlaySceneIdRightAfter;
            const sceneId = usingOverlaySceneAfter
                ? overlaySceneIdRightAfter
                : endNodeOverlaySceneId;
            if (!sceneId) {
                throw new Error('Overlay scene ID not found');
            }
            const overlay = luminary.getSceneById(sceneId);
            if (!overlay) {
                throw new Error('Overlay missing from luminary!');
            }
            endTime = usingOverlaySceneAfter ? overlay.startTime : overlay.endTime;
        }

        return { startTime, endTime };
    },
);

/**
 * Often, we have a range of TranscriptWordNodes in the transcript, and we want to convert that
 * into the start/end time represented by that range of nodes. Eg. the user selects a couple words
 * and wants to cut that part of the video out.
 *
 * This is actually more complicated than it might seem - we want to watch out for overlay boundaries,
 * and cut boundaries, to line up with those.
 *
 * @returns { startTime, endTime } - the start and end time represented by the startNode and endNode (in transcript time)
 */
export const $getTimeIndicatedBy = createLuminaryOperation(
    (
        { luminary },
        startNode: TranscriptWordNode,
        endNode: TranscriptWordNode,
        sceneGroupId: string,
    ) => {
        const wordBeforeFirstSelectedWord = $getPreviousTranscriptWordNode(startNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });
        const wordAfterLastSelectedWord = $getNextTranscriptWordNode(endNode, {
            includeSpaces: false,
            includeTrimmedWords: false,
        });

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const preExistingCuts = getVoiceDrivenBaseAssetCutRegions(sceneGroup);

        const sceneStartTimes = sceneGroup.scenes.map((scene) => {
            return translateVideoTimeToTranscriptTime(
                scene.startTime,
                voiceDrivenAssetPlaybackRegions,
                voiceDrivenAssetSourceDuration,
                sceneGroup.startTime,
            );
        });

        let startTime = 0;
        if (wordBeforeFirstSelectedWord) {
            // if there is a scene start time between the first word selected and the word just before the
            // selection, then the selection reflects the scene start time
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                ]);
            });
            // similar for pre-existing cuts
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    wordBeforeFirstSelectedWord.endTime,
                    startNode.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                startTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                startTime = preExistingCutBetween[1];
            } else {
                // otherwise, the selection reflects the midpoint between the two words
                startTime = (wordBeforeFirstSelectedWord.endTime + startNode.startTime) / 2;
            }
        }

        // same for endTime
        let endTime = voiceDrivenAssetSourceDuration;
        if (wordAfterLastSelectedWord) {
            const sceneStartTimeBetween = sceneStartTimes.find((sceneStartTime) => {
                return thisIsWithinRange(sceneStartTime, [
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                ]);
            });
            const preExistingCutBetween = preExistingCuts.find(([startTime, endTime]) => {
                return theseAreOverlapping(
                    endNode.endTime,
                    wordAfterLastSelectedWord.startTime,
                    startTime,
                    endTime,
                );
            });
            if (sceneStartTimeBetween) {
                endTime = sceneStartTimeBetween;
            } else if (preExistingCutBetween) {
                endTime = preExistingCutBetween[0];
            } else {
                endTime = (endNode.endTime + wordAfterLastSelectedWord.startTime) / 2;
            }
        }

        return { startTime, endTime };
    },
);

export const $getTimeIndicatedBySelection = createLuminaryOperation(
    ({ call }, sceneGroupId: string) => {
        const selectedNodes = $getSelectedNodes();
        const selectedWords = selectedNodes.filter($isTranscriptWordNode);
        const firstWord = selectedWords[0];
        const lastWord = selectedWords[selectedWords.length - 1];
        return call($getTimeIndicatedBy, firstWord, lastWord, sceneGroupId);
    },
);

/**
 * This operation will play the scene.
 */
export function playScene(payload: StartScenePreviewPlaybackPayload) {
    return () => {
        SlideEventEmitter.emit(SLIDE_EVENTS.START_SCENE_PREVIEW_PLAYBACK, payload);
    };
}

export const syncTranscriptToLuminaryOperation = createLuminaryOperation(
    (
        { luminary, getState },
        editor: LexicalEditor,
        sceneGroupId: string,
        typeOfTranscript: TypeOfTranscript,
    ) => {
        const state = getState();
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const words = getTranscriptWords(editor) as TranscriptWordData[];

        switch (typeOfTranscript) {
            // update the original transcript
            case TypeOfTranscript.MEDIA_TRANSCRIPT_TAB:
            case TypeOfTranscript.CLIP_CREATOR_MAIN:
            case TypeOfTranscript.CLIP_CREATOR_CLIP: {
                const transcript = getTranscriptById(state, sceneGroupId);
                if (!transcript) {
                    return;
                }
                const language = transcript.get('language') ?? LanguageEnum.XX;
                const separateWordsWithSpaces = shouldSeparateWordsWithSpaces(state, language);
                const textDirection = getLanguageTextDirection(state, language);

                const transcriptData: LuminaryTranscriptData = {
                    language,
                    textDirection,
                    words,
                    separateWordsWithSpaces,
                };
                sceneGroup.originalTranscript.transcriptData = transcriptData;

                // when our transcript is not translated, then we also use it for our captions
                if (!getTranscriptTranslationLanguage(state, sceneGroupId)) {
                    sceneGroup.captions.transcript.transcriptData = transcriptData;
                }
                break;
            }
            // update the translated transcript
            case TypeOfTranscript.TRANSLATION: {
                const language = getTranscriptTranslationLanguage(state, sceneGroupId);
                if (!language) {
                    return;
                }
                const separateWordsWithSpaces = shouldSeparateWordsWithSpaces(state, language);
                const textDirection = getLanguageTextDirection(state, language);

                sceneGroup.captions.transcript.transcriptData = {
                    language,
                    textDirection,
                    words,
                    separateWordsWithSpaces,
                };
                break;
            }
            default: {
                throw new Error('Unknown type of transcript');
            }
        }
    },
);

export const showTranscriptLanguageWarning = (langCode: string) => {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        const textDirection = getLanguageTextDirection(getState(), langCode);
        if (textDirection === TextDirectionEnum.RTL) {
            Toast.error(
                "Lumen5 doesn't currently support this language. Your captions may not show up properly.",
                'Unsupported language detected',
            );
        }
    };
};

// Todo: This is temporarily here instead of 'transcript-types' to avoid strict null check errors. This should be generated from the backend
export type EditVoiceoverTaskResponse = {
    transcript: EditedTranscriptWord[];
    audio_asset: AudioAssetModel;
};
export enum WhatToDoWithEditAIVoiceoverResult {
    MaintainOverlayToWordRelationships = 'MaintainOverlayToWordRelationships',
    RebalanceOverlaysByDurationPercentage = 'RebalanceOverlaysByDurationPercentage',
}

export const editAIVoiceoverOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        {
            sceneGroupId,
            transcript,
            langCode,
            originalVoiceoverAssetSourceData,
            whatToDoWithEditAIVoiceoverResult,
            newVoice,
            newVoiceSpeed,
            typeOfTranscript = TypeOfTranscript.MEDIA_TRANSCRIPT_TAB,
        }: {
            sceneGroupId: string;
            transcript: EditedTranscriptWordRequest[];
            langCode: LanguageEnum;
            originalVoiceoverAssetSourceData: AudioAssetSourceData;
            whatToDoWithEditAIVoiceoverResult: WhatToDoWithEditAIVoiceoverResult;
            newVoice?: string;
            newVoiceSpeed?: number | null;
            typeOfTranscript?: TypeOfTranscript;
        },
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        dispatch(
            markSceneGroupAsLoading({
                sceneGroupId,
                type: SCENE_GROUP_LOADING_TYPES.RECOMMENDING_OVERLAYS,
                context: { loadingMsg: LOADING_SCENE_MSG.AI_VOICEOVER_EDIT },
            }),
        );
        try {
            const ws = WebSocketClient.getInstance();
            const response = await ApiService.editVoiceoverUpdate({
                body: {
                    full_transcript: transcript,
                    language: langCode,
                    original_audio_asset_source_data: originalVoiceoverAssetSourceData,
                    trim_end_millis: getVoiceDrivenBaseAssetEndTime(sceneGroup),
                    trim_start_millis: getVoiceDrivenBaseAssetStartTime(sceneGroup),
                    context_select_full_transcript:
                        typeOfTranscript === TypeOfTranscript.TRANSLATION,
                    new_voice: newVoice,
                    new_voice_speed: newVoiceSpeed,
                    video_id: getVideoId(getState()),
                },
            });
            const result = await ws.waitForMessage<EditVoiceoverTaskResponse>(
                response.data.async_operation_uuid,
                TaskResultMessageTypeEnum.TASK_RESULT,
                AsyncOperationNameEnum.GENERATE_EDITED_VOICEOVER,
            );

            // While transcript and luminary are updated, we don't want the LuminarySyncPlugin to run
            // So set lexical to controlling its own state until its done
            dispatch(startLexicalControllingItsOwnState({ sceneGroupId }));

            switch (whatToDoWithEditAIVoiceoverResult) {
                case WhatToDoWithEditAIVoiceoverResult.MaintainOverlayToWordRelationships: {
                    await call(
                        recreateEditorAndMaintainOverlayToWordRelationships,
                        result,
                        sceneGroupId,
                        typeOfTranscript,
                        systemTriggered,
                        parentActionGroup,
                    );
                    break;
                }
                case WhatToDoWithEditAIVoiceoverResult.RebalanceOverlaysByDurationPercentage: {
                    await call(
                        recreateEditorAndAlignScenesByDurationPercentageOperation,
                        result,
                        sceneGroupId,
                        typeOfTranscript,
                    );
                    break;
                }
                default: {
                    throw new Error('Unknown value for whatToDoWithEditAIVoiceoverResult');
                }
            }
            // Now that all scene durations are updated, update the scene group duration
            await updateSceneGroupDurationToEqualVoiceDrivenAssetDuration({ sceneGroup });
        } catch (e) {
            // Make sure to throw any error so that the caller can handle and undo any changes
            dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
            dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));
            call(resetLuminaryAndLexicalToPresentCheckpointOperation);
            throw e;
        }
        // All updates are done, let lexical control its own state again and allow LuminarySuncPlugin
        // to sync changes
        dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
        dispatch(markSceneGroupAsFinishedLoading({ sceneGroupId }));

        // Checkpoint transcript and video data
        call(
            checkpointVideoAndTranscriptOperation,
            sceneGroupId,
            { waitForLuminary: true },
            systemTriggered,
            parentActionGroup,
        );

        // Handle tracking
        const regeneratedCharLength = transcript.reduce((len, word) => {
            if (word.contextSelected || word.userSelected) {
                return len;
            }
            return len + word.word.length;
        }, 0);

        BatchAnalytics.track(
            'Elevenlabs Character Usage',
            {},
            { num_characters: regeneratedCharLength },
        );
    },
);

/**
 * After calling the backend to regenerate the AIVO, we need to reconcile the new transcript with the overlays
 * and trim points in the user's video.
 *
 * The function does that by
 *
 * - overlays: each scene's duration represents a percentage of the total sceneGroup's duration. We calculate
 * each scene's initial percentage duration, and use that to calculate a target duration in the sceneGroup with
 * the new voiceoverAsset applied. We then fine-tune that target duration to make sure that scene boundaries fall
 * between words in the transcript.
 * - trim points: We copy the start trim time from the original voiceoverAsset, and use the calculation of the
 * scenes' times to calculate the new end trim time.
 *
 * This is one of two ways we can reconcile overlays/trims after regenerating an AI VO.
 * The other way is to use recreateEditorAndMaintainOverlayToWordRelationships.
 *
 * We'd want to use this method when the words in the transcript are TOTALLY different from the original transcript.
 * For example: when translating to a new language - all the words are different from the original transcript. In
 * this case, we can't maintain the overlay-to-word relationships because the words are different.
 */
export const recreateEditorAndAlignScenesByDurationPercentageOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        result: EditVoiceoverTaskResponse,
        sceneGroupId: string,
        typeOfTranscript: TypeOfTranscript,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const editor =
            typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
                ? getEditorFromSceneGroupId(getState(), sceneGroupId)
                : getTranslatedEditorBySceneGroupId(getState(), sceneGroupId);
        const mainEditor = getEditorFromSceneGroupId(getState(), sceneGroupId);

        if (!editor || !mainEditor) {
            return;
        }

        // the trimmed out words are never sent to ElevenLabs, so we can guarantee that the start trim time won't change
        // however, the words that are trimmed "in" could change their timings, so we need to recalculate the end trim time
        const initialEndTime = getVoiceDrivenBaseAssetEndTime(sceneGroup);
        const initialStartTime = getVoiceDrivenBaseAssetStartTime(sceneGroup);
        const hasEndTrim = !equalWithinEpsilon(
            initialEndTime,
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
        );

        const initialSceneGroupDuration = sceneGroup.duration;
        const sceneDurationPercentageMap = sceneGroup.scenes.reduce(
            (durationPercentageMap, scene) => {
                durationPercentageMap[scene.id] = scene.duration / initialSceneGroupDuration;
                return durationPercentageMap;
            },
            {} as Record<string, number>,
        );

        const transcriptResult = convertTranscriptWordsToMillis(result.transcript);
        // the backend rounds the duration to the nearest second. If the backend
        // coincidentally rounded the duration down, there could be words AFTER the
        // end of the audio asset, which should be impossible. Eg. if the audio is
        // actually 10.4 seconds long, there could be a word at 10.2 - 10.3 seconds.
        // That duration would be rounded down by the backend to 10 seconds.
        // To avoid words after the end of the audio, just take the max of the two:
        const audioAssetDurationMillis = Math.max(
            getTranscriptDuration(transcriptResult),
            result.audio_asset.duration * 1000.0,
        );

        const audioAssetSourceData = {
            ...result.audio_asset,
            duration: audioAssetDurationMillis / 1000.0,
        };

        const firstTrimmedOutWordId = editor.getEditorState().read(() => {
            const lastWordShowing = $getLastTranscriptWordShowing(initialEndTime);
            const firstTrimmedOutWord = $getNextTranscriptWordNode(lastWordShowing);
            return firstTrimmedOutWord?.id;
        });

        await updateEditor(mainEditor, () => {
            // replace the contents of the editor with the new transcript
            const silenceNodeGenerationMethod =
                typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
                    ? SilenceNodeGenerationMethod.GenerateFromTranscriptWord
                    : SilenceNodeGenerationMethod.DoNotGenerate;
            $generateEditorFromTranscript(
                transcriptResult,
                shouldSeparateTranscriptWordsWithSpaces(getState(), sceneGroupId, typeOfTranscript),
                silenceNodeGenerationMethod,
            );

            // now, find the trimmed-out word in the new transcript
            let newEndTime = audioAssetDurationMillis;
            if (firstTrimmedOutWordId) {
                const newFirstTrimmedOutWord = $getWordNodeByWordId(firstTrimmedOutWordId);
                if (!newFirstTrimmedOutWord) {
                    throw new Error(
                        'Could not find trimmed out word in new transcript. It should have been preserved by the backend',
                    );
                }
                const newLastWordShowing = $getPreviousTranscriptWordNode(newFirstTrimmedOutWord);
                if (newLastWordShowing) {
                    newEndTime =
                        (newLastWordShowing.endTime + newFirstTrimmedOutWord.startTime) / 2.0;
                }
            }

            const newDuration = newEndTime - initialStartTime;
            const startTime = getVoiceDrivenBaseAssetStartTime(sceneGroup);
            const playbackRegions: PlaybackRegions = [[startTime, startTime + newDuration]]; // we only support one playback region for AI VO

            luminary.batchRun(() => {
                sceneGroup.voiceoverAsset.sourceData = audioAssetSourceData;

                let timeUsed = 0;
                sceneGroup.scenes.forEach((scene) => {
                    const nextScene = scene.nextScene;
                    const isLastSceneInSceneGroup =
                        !nextScene || nextScene.sceneGroup.id !== sceneGroupId;

                    const targetDuration = sceneDurationPercentageMap[scene.id] * newDuration;
                    const targetEndTime = timeUsed + targetDuration;
                    let fineOverlayBoundaryTime = $getFineOverlayBoundaryTimeCloseTo(
                        targetEndTime,
                        playbackRegions,
                        getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
                        { getIdealOverlayBoundaryTime: true },
                    );
                    if (isLastSceneInSceneGroup) {
                        fineOverlayBoundaryTime = newDuration;
                    }
                    if (fineOverlayBoundaryTime === timeUsed) {
                        // we are putting this overlay boundary in the exact same spot as the last one
                        // that won't work! Shift it over one word
                        const word = $getNextWordAtTime(fineOverlayBoundaryTime); // `word` is going to be the only word in this overlay
                        if (!word) {
                            // We are already at the end of the transcript! There's no space for
                            // another scene. Just delete this scene!
                            sceneGroup.removeScene(scene.id);
                            return;
                        }
                        const nextWord = $getNextTranscriptWordNode(word);
                        if (word && nextWord) {
                            fineOverlayBoundaryTime = (word.endTime + nextWord.startTime) / 2;
                        } else {
                            // we are at the end of the transcript! Just move it to the end
                            fineOverlayBoundaryTime = newDuration;
                        }
                    }
                    scene.duration = fineOverlayBoundaryTime - timeUsed;
                    timeUsed = fineOverlayBoundaryTime;
                });

                // now, all of the scene durations have been updated. We also need to set the trim times, if the scene was trimmed
                if (initialStartTime) {
                    setVoiceDrivenBaseAssetStartTime(sceneGroup, initialStartTime);
                }
                if (hasEndTrim) {
                    const voiceDrivenAssetPlaybackRegions =
                        getVoiceDrivenAssetPlaybackRegions(sceneGroup);
                    const voiceDrivenAssetSourceDuration =
                        getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                    const newEndTime = translateSceneGroupTimeToTranscriptTime(
                        sceneGroup.duration,
                        voiceDrivenAssetPlaybackRegions,
                        voiceDrivenAssetSourceDuration,
                        { includeTrailingCut: false },
                    );
                    setVoiceDrivenBaseAssetEndTime(sceneGroup, newEndTime);
                }
            });
        });
    },
);

/**
 * After calling the backend to regenerate the AIVO, we need to reconcile the new transcript with the overlays
 * and trim points in the user's video.
 *
 * This function assumes that the words in the new transcript are the same as the old transcript. It
 * leverages that assumption to make sure that overlay boundaries are placed between the same words that they
 * previously existed between.
 *
 * This is one of two ways we can reconcile overlays/trims after regenerating an AI VO.
 * The other way is to use recreateEditorAndAlignScenesByDurationPercentageOperation, which does not assume
 * that the words in the new transcript are the same as the old transcript.
 */
export const recreateEditorAndMaintainOverlayToWordRelationships = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        result: EditVoiceoverTaskResponse,
        sceneGroupId: string,
        typeOfTranscript: TypeOfTranscript,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || !isAIVoiceoverSceneGroup(sceneGroup)) {
            return;
        }

        const editor =
            typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
                ? getEditorFromSceneGroupId(getState(), sceneGroupId)
                : getTranslatedEditorBySceneGroupId(getState(), sceneGroupId);

        if (!editor) {
            return;
        }

        const transcriptResult = convertTranscriptWordsToMillis(result.transcript);

        // the backend rounds the duration to the nearest second. If the backend
        // coincidentally rounded the duration down, there could be words AFTER the
        // end of the audio asset, which should be impossible. Eg. if the audio is
        // actually 10.4 seconds long, there could be a word at 10.2 - 10.3 seconds.
        // That duration would be rounded down by the backend to 10 seconds.
        // To avoid words after the end of the audio, just take the max of the two:
        const audioAssetDurationMillis = Math.max(
            getTranscriptDuration(transcriptResult),
            result.audio_asset.duration * 1000,
        );
        const audioAssetSourceData = {
            ...result.audio_asset,
            duration: audioAssetDurationMillis / 1000.0,
        };

        const baseAssetStartTime = getVoiceDrivenBaseAssetStartTime(sceneGroup);
        const baseAssetEndTime = getVoiceDrivenBaseAssetEndTime(sceneGroup);
        const hasEndTrim = !equalWithinEpsilon(
            baseAssetEndTime,
            getVoiceDrivenBaseAssetSourceDuration(sceneGroup),
        );

        const scenes = sceneGroup.scenes;

        // Store new word in a map for easy access
        const newWords = transcriptResult.reduce(
            (acc, word) => {
                return { ...acc, [word.id]: word };
            },
            {} as Record<string, EditedTranscriptWord>,
        );

        // Get the original first and last word nodes of each scene
        // This will be used to calculate the new scene times
        const interestPoints = call(
            calculateInterestPointTimesForEntireSceneGroupOperation,
            sceneGroupId,
        );
        const firstAndLastWordsBySceneId = editor.getEditorState().read(() => {
            return $getFirstLastWordsBySceneId(scenes);
        });

        const leftTrimmedOutWordId = baseAssetStartTime
            ? editor.getEditorState().read(() => {
                  const firstTrim = $findMatchWithin($getRoot(), $isTrimElementNode);
                  if (!firstTrim) {
                      return undefined;
                  }
                  const wordNode = $getLastNonEmptyWordWithin(firstTrim);
                  return wordNode?.id;
              })
            : undefined;

        await updateEditor(editor, () => {
            // Next, rebuild the transcript. Note: the new transcript words will have the same ids
            const silenceNodeGenerationMethod =
                typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
                    ? SilenceNodeGenerationMethod.GenerateFromTranscriptWord
                    : SilenceNodeGenerationMethod.DoNotGenerate;
            $generateEditorFromTranscript(
                transcriptResult,
                shouldSeparateTranscriptWordsWithSpaces(getState(), sceneGroupId, typeOfTranscript),
                silenceNodeGenerationMethod,
            );
        });

        // Update luminary
        luminary.batchRun(() => {
            // Update audio asset source data
            sceneGroup.voiceoverAsset.sourceData = audioAssetSourceData;

            // Update scene durations
            const sceneIds = sceneGroup.scenes.map((scene) => scene.id);

            let newStartTime = baseAssetStartTime;
            let newEndTime = 0;
            for (const sceneId of sceneIds) {
                if (!firstAndLastWordsBySceneId[sceneId]) {
                    throw new Error('First or last word node of scene not found');
                }
                const lastWordNode = firstAndLastWordsBySceneId[sceneId].lastWordNode;

                const scene = luminary.getSceneById(sceneId);
                if (!scene) {
                    throw new Error('Scene not found');
                }

                // Calculate the new scene end time
                const nextScene = scene.nextScene;
                if (!nextScene || nextScene.sceneGroup.id !== sceneGroupId) {
                    // We are working on the last scene in the scene group
                    if (hasEndTrim) {
                        const lastWordId = lastWordNode.id;
                        if (!lastWordId) {
                            throw new Error('Last word id not found');
                        }
                        const lastWordOfSceneGroup = newWords[lastWordId];
                        newEndTime = newWords[lastWordOfSceneGroup.id].endTime;
                        const nextWord = editor.getEditorState().read(() => {
                            return $getNextWordAtTime(newEndTime);
                        });
                        if (nextWord) {
                            newEndTime = (newEndTime + nextWord.startTime) / 2;
                        }
                    } else {
                        newEndTime = audioAssetDurationMillis;
                    }
                } else {
                    // Get the word id of the last word of the scene
                    const lastWordId = lastWordNode.id;
                    if (!lastWordId) {
                        throw new Error('Last word id not found');
                    }
                    const lastWordOfScene = newWords[lastWordId];

                    // Get the word id of the first word of the next scene
                    if (!firstAndLastWordsBySceneId[nextScene.id]) {
                        throw new Error('First or last word node of next scene not found');
                    }
                    const firstWordIdOfNextScene =
                        firstAndLastWordsBySceneId[nextScene.id].firstWordNode.id;
                    const firstWordOfNextScene = newWords[firstWordIdOfNextScene];

                    newEndTime = (firstWordOfNextScene.startTime + lastWordOfScene.endTime) / 2;
                }

                const newDuration = newEndTime - newStartTime;
                scene.duration = newDuration;
                newStartTime = newEndTime;
            }

            // now, all of the scene durations have been updated. We also need to set the trim times, if the scene was trimmed
            if (baseAssetStartTime) {
                // the trimmed out words are never sent to ElevenLabs, so we can guarantee that the start trim time won't change
                const [firstWordInSceneGroupStartTime, gapBetweenWordsAtTrim] = editor
                    .getEditorState()
                    .read(() => {
                        let trimmedOutWordEndTime = 0;
                        let firstWordNodeInSceneGroup: TranscriptWordNode | null = $dfs()
                            .map(({ node }) => node)
                            .filter($isTranscriptWordNode)[0];

                        if (leftTrimmedOutWordId) {
                            const trimmedOutWord = $getWordNodeByWordId(leftTrimmedOutWordId);
                            if (!trimmedOutWord) {
                                throw new Error('Left trimmed out word not found');
                            }
                            trimmedOutWordEndTime = trimmedOutWord.endTime;
                            firstWordNodeInSceneGroup = $getNextTranscriptWordNode(trimmedOutWord);
                        }

                        if (!firstWordNodeInSceneGroup) {
                            throw new Error('First word node in scene group not found');
                        }
                        return [
                            firstWordNodeInSceneGroup.startTime,
                            (firstWordNodeInSceneGroup.startTime + trimmedOutWordEndTime) / 2,
                        ];
                    });

                if (firstWordInSceneGroupStartTime < baseAssetStartTime) {
                    // user must've deleted some words and shorted the gap between the words around the trim border
                    // so we need to move the trim border earlier to adjust for this new gap
                    setVoiceDrivenBaseAssetStartTime(sceneGroup, gapBetweenWordsAtTrim);
                } else {
                    setVoiceDrivenBaseAssetStartTime(sceneGroup, baseAssetStartTime);
                }
            }
            if (hasEndTrim) {
                const voiceDrivenAssetPlaybackRegions =
                    getVoiceDrivenAssetPlaybackRegions(sceneGroup);
                const voiceDrivenAssetSourceDuration =
                    getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
                const newEndTime = translateSceneGroupTimeToTranscriptTime(
                    sceneGroup.duration,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    { includeTrailingCut: false },
                );
                setVoiceDrivenBaseAssetEndTime(sceneGroup, newEndTime);
            }

            call(
                reapplyInterestPointsOperation,
                interestPoints,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

// Wrapper for editAIVoiceoverOperation that selects the entire transcript for edit
export const editFullAIVoiceoverOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        newVoiceSpeed: number | null = null,
        selectedAIVoiceId?: string,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }
        const originalVoiceoverAssetSourceData = getVoiceDrivenAssetSourceData(
            sceneGroup,
        ) as AudioAssetSourceData;
        if (!originalVoiceoverAssetSourceData) {
            return;
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }
        const userSelectedWordIds = undefined; // Pass undefined to select all words
        const fullTranscript = getFullTranscriptFromEditor(editor, userSelectedWordIds, {
            includeSilence: true,
        });
        const langCode = getOriginalTranscriptLanguage(getState(), sceneGroupId);
        await call(
            editAIVoiceoverOperation,
            {
                newVoice: selectedAIVoiceId,
                newVoiceSpeed: newVoiceSpeed,
                sceneGroupId,
                transcript: fullTranscript,
                langCode,
                originalVoiceoverAssetSourceData,
                whatToDoWithEditAIVoiceoverResult:
                    WhatToDoWithEditAIVoiceoverResult.MaintainOverlayToWordRelationships,
            },
            systemTriggered,
            parentActionGroup,
        );
    },
);

export function translateTranscript(
    targetLanguage: LanguageLocalesEnum,
    sceneGroupId: string,
    videoId: number | null,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        const originalTranscriptLanguage = getOriginalTranscriptLanguage(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('You must have an editor to translate the transcript!');
        }
        const supportedLanguages = getSupportedLanguages(getState());
        const parentLanguage = convertLocaleToLanguageCode(supportedLanguages, targetLanguage);
        if (!parentLanguage) {
            throw new Error('Target language is not supported');
        }
        const transcript = getFullTranscriptFromEditor(editor);
        const translatedTranscript = await translateText(
            transcript,
            targetLanguage,
            originalTranscriptLanguage,
            videoId,
        );

        // use the editor instead of editorState
        const translatedTranscriptEditor = await dispatch(
            buildTranslatedTranscript(translatedTranscript, parentLanguage),
        );

        dispatch(
            storeTranslatedEditor({
                sceneGroupId,
                editor: translatedTranscriptEditor,
                langCode: parentLanguage,
                lexicalControlsItsOwnState: false,
            }),
        );
    };
}

export const applyTranslatedCaptionTranscriptOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        sceneGroupId: string,
        translatedLanguage: LanguageEnum,
        parentActionGroup: string = uuid(),
        systemTriggered: boolean = false,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        const isAIVoiceover = isAIVoiceoverSceneGroup(sceneGroup);
        const translatedTranscriptEditor = getTranslatedEditorBySceneGroupId(
            getState(),
            sceneGroup.id,
        );
        if (!translatedTranscriptEditor) throw new Error('Translated transcript editor not found');
        if (isAIVoiceover) {
            const transcript = getFullTranscriptFromEditor(translatedTranscriptEditor);

            await call(
                editAIVoiceoverOperation,
                {
                    sceneGroupId,
                    transcript,
                    langCode: translatedLanguage,
                    originalVoiceoverAssetSourceData: sceneGroup.voiceoverAsset
                        ?.sourceData as unknown as AudioAssetSourceData,
                    whatToDoWithEditAIVoiceoverResult:
                        WhatToDoWithEditAIVoiceoverResult.RebalanceOverlaysByDurationPercentage,
                    typeOfTranscript: TypeOfTranscript.TRANSLATION,
                },
                systemTriggered,
                parentActionGroup,
            );
            // after the edit is complete, we can remove the translation editor instance from redux.
            // That's because for AI VO, we replace the contents of the Main editor with the translation,
            // rather than having a separate translation editor.
            dispatch(removeTranslatedEditor({ sceneGroupId }));

            // now that the main transcript has been updated, we also need to update the language of the
            // main transcript
            dispatch(
                setTranscriptLanguage({
                    sceneGroupId: sceneGroupId,
                    language: translatedLanguage,
                    systemTriggered,
                    parentActionGroup,
                }),
            );
        } else {
            // store the translation into redux
            dispatch(
                storeTranscriptTranslationsFromEditor(
                    translatedTranscriptEditor,
                    translatedLanguage,
                    sceneGroup.id,
                    systemTriggered,
                    parentActionGroup,
                ),
            );

            // sync the translation transcript back to luminary
            call(
                syncTranscriptToLuminaryOperation,
                translatedTranscriptEditor,
                sceneGroup.id,
                TypeOfTranscript.TRANSLATION,
            );
        }
    },
);

export const translateVideoTranscriptCaptionOperation = createLuminaryOperation(
    async (
        { luminary, dispatch, getState, call },
        targetLanguage: LanguageLocalesEnum,
        videoId: number,
        langSupportAIVoiceover: boolean,
        systemTriggered: boolean = false,
        parentActionGroup: string = uuid(),
    ) => {
        const voicedrivenSceneGroups = luminary.sceneGroups.filter((sceneGroup) =>
            isVoiceDrivenSceneGroup(sceneGroup),
        );
        const supportedLanguages = getSupportedLanguages(getState());
        const parentLanguage = convertLocaleToLanguageCode(supportedLanguages, targetLanguage);
        if (!parentLanguage) {
            throw new Error('Target language is not supported');
        }
        const allPromises: Promise<void>[] = [];
        voicedrivenSceneGroups.forEach((sceneGroup) => {
            if (!langSupportAIVoiceover && isAIVoiceoverSceneGroup(sceneGroup)) {
                return;
            }

            const doTheTranslate = async () => {
                await dispatch(translateTranscript(targetLanguage, sceneGroup.id, videoId));
                await call(
                    applyTranslatedCaptionTranscriptOperation,
                    sceneGroup.id,
                    parentLanguage,
                    parentActionGroup,
                    systemTriggered,
                );
            };
            allPromises.push(doTheTranslate());
        });

        try {
            await Promise.all(allPromises);
        } catch {
            Toast.error('Failed to apply translation');
        }
    },
);

export const cutSilencesOperation = createLuminaryOperation(
    (
        { getState, call, luminary },
        sceneGroupId: string,
        minCuttableSilenceDuration: number,
        systemTriggered: boolean,
        parentActionGroup: string,
    ): number => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return 0;
        }
        return editor.getEditorState().read(() => {
            const silenceNodes = $nodesOfType(SilenceNode);
            const TRAVERSAL_SETTINGS = {
                includeSilence: true,
                includeSpaces: false,
                includeTrimmedWords: false,
            };
            const silenceSpans: [SilenceNode, SilenceNode][] = [];
            const silenceNodesVisited = new Set();
            silenceNodes.forEach((silenceNode) => {
                if ($isTrimmedOut(silenceNode)) {
                    // this is already cut, so we can skip
                    return;
                }
                if (silenceNodesVisited.has(silenceNode.getKey())) {
                    // we've already included this silence node in a previous cut
                    // so we can skip it
                    return;
                }

                silenceNodesVisited.add(silenceNode.getKey());

                let beginningOfSilence = silenceNode;
                let endOfSilence = silenceNode;

                // find the beginning of this group of silence nodes
                let leftTestingNode = $getPreviousTranscriptWordNode(
                    silenceNode,
                    TRAVERSAL_SETTINGS,
                );
                while ($isSilenceNode(leftTestingNode)) {
                    silenceNodesVisited.add(leftTestingNode.getKey());
                    beginningOfSilence = leftTestingNode;
                    leftTestingNode = $getPreviousTranscriptWordNode(
                        leftTestingNode,
                        TRAVERSAL_SETTINGS,
                    );
                }

                // and find the end of this group of silence nodes
                let rightTestingNode = $getNextTranscriptWordNode(silenceNode, TRAVERSAL_SETTINGS);
                while ($isSilenceNode(rightTestingNode)) {
                    silenceNodesVisited.add(rightTestingNode.getKey());
                    endOfSilence = rightTestingNode;
                    rightTestingNode = $getNextTranscriptWordNode(
                        rightTestingNode,
                        TRAVERSAL_SETTINGS,
                    );
                }

                // now we have the beginning and end of the silence span
                silenceSpans.push([beginningOfSilence, endOfSilence]);
            });

            let cumulativeCutDuration = 0;
            luminary.batchRun(() => {
                silenceSpans.forEach(([beginningOfSilence, endOfSilence]) => {
                    const { startTime, endTime } = call(
                        $getTimeIndicatedBy,
                        beginningOfSilence,
                        endOfSilence,
                        sceneGroupId,
                    );
                    const cutDuration = endTime - startTime;
                    if (cutDuration < minCuttableSilenceDuration) {
                        // if the cut duration is less than the minimum, we don't cut it
                        return;
                    }
                    cumulativeCutDuration += cutDuration;
                    call(
                        cutVoicedrivenSceneGroupOperation,
                        sceneGroupId,
                        startTime,
                        endTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                });
            });
            return cumulativeCutDuration;
        });
    },
);

export const cutFillerWordsOperation = createLuminaryOperation(
    (
        { getState, luminary, call },
        sceneGroupId: string,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return 0;
        }
        return editor.getEditorState().read(() => {
            const fillerWords = $nodesOfType(TranscriptWordNode).filter((node) => {
                return node.filler && !$isTrimmedOut(node);
            });
            if (fillerWords.length === 0) {
                return 0;
            }

            luminary.batchRun(() => {
                fillerWords.forEach((fillerWord) => {
                    const { startTime, endTime } = call(
                        $getTimeIndicatedBy,
                        fillerWord,
                        fillerWord,
                        sceneGroupId,
                    );
                    call(
                        cutVoicedrivenSceneGroupOperation,
                        sceneGroupId,
                        startTime,
                        endTime,
                        systemTriggered,
                        parentActionGroup,
                    );
                });
            });
            return fillerWords.length;
        });
    },
);

export const enterEditTimingModeOperation = createLuminaryOperation(
    ({ getState, luminary, dispatch }, { assetId, sceneId, sceneGroupId }) => {
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            throw new Error('Editor not found');
        }

        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            throw new Error('Scene group not found');
        }
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            throw new Error('Scene not found');
        }
        const asset = scene.getAssetById(assetId);
        if (!asset) {
            throw new Error('Asset not found');
        }
        dispatch(startLexicalControllingItsOwnState({ sceneGroupId }));
        dispatch(startTimingMode({ assetId, sceneId, sceneGroupId }));

        editor.dispatchCommand(ENTER_TIMING_MODE_COMMAND, undefined);
    },
);

export const exitEditTimingModeOperation = createLuminaryOperation(
    ({ getState, luminary, dispatch }) => {
        const sceneGroupId = getTimingModeSceneGroupId(getState());
        if (!sceneGroupId) {
            return;
        }

        if (!isTimingModeActive(getState())) {
            return;
        }
        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        dispatch(endTimingMode());
        editor.dispatchCommand(EXIT_TIMING_MODE_COMMAND, undefined);
        dispatch(endLexicalControllingItsOwnState({ sceneGroupId }));
    },
);

export const calculateInterestPointTimesForEntireSceneGroupOperation = createLuminaryOperation(
    ({ luminary, getState, call }, sceneGroupId: string): InterestPointTime[] => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return [];
        }
        return sceneGroup.scenes.flatMap((scene) => {
            return call(calculateInterestPointTimesOperation, scene.id);
        });
    },
);

/**
 * Calculates interest points for assets with user-overridden timing in a scene.
 * Interest points represent where asset intro/outro times are relative to transcript words.
 */
export const calculateInterestPointTimesOperation = createLuminaryOperation(
    ({ luminary, getState }, sceneId: string): InterestPointTime[] => {
        const scene = luminary.getSceneById(sceneId);
        if (!scene) {
            return [];
        }

        const sceneGroup = scene.sceneGroup;
        const assetsWithOverriddenTiming = getAssetsWithTimesOverridden(getState(), sceneId);
        if (assetsWithOverriddenTiming.length === 0) {
            return [];
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroup.id);
        if (!editor) {
            return [];
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
        const sceneGroupStartTime = sceneGroup.startTime;

        const interestPoints: InterestPointTime[] = [];

        return editor.getEditorState().read(() => {
            for (const assetId of assetsWithOverriddenTiming) {
                const asset = scene.getAssetById(assetId);
                if (!asset) {
                    continue;
                }

                const assetStartTimeVideoTime = scene.startTime + asset.introAnimationOffset;
                const assetEndTimeVideoTime = scene.endTime - asset.outroAnimationOffset;

                const introTimeTranscript = translateVideoTimeToTranscriptTime(
                    assetStartTimeVideoTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroupStartTime,
                );
                const outroTimeTranscript = translateVideoTimeToTranscriptTime(
                    assetEndTimeVideoTime,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroupStartTime,
                );

                const prevWordForIntro = $getPreviousWordAtTime(introTimeTranscript);
                const nextWordForIntro = $getNextWordAtTime(introTimeTranscript);

                const shouldCreateInterestPoint = (
                    prevWord: TranscriptWordNode,
                    nextWord: TranscriptWordNode,
                    offset: number,
                ) => {
                    const prevWordOverlay = $getNearestNodeOfType(prevWord, BaseOverlayElementNode);
                    const nextWordOverlay = $getNearestNodeOfType(nextWord, BaseOverlayElementNode);
                    if (prevWordOverlay && nextWordOverlay && prevWordOverlay === nextWordOverlay) {
                        // the point is between two words in a single overlay. Definitely an interest point.
                        return true;
                    }
                    // the point is next to an overlay boundary. It could be an interest point, or maybe the scene design has
                    // an offset and we should not override it (eg. the user changed the intro animation offset, but not
                    // the outro animation offset for this asset). Only create an interest point if the offset is large, which
                    // means it's more likely that the user has explicitly set the offset.
                    return offset > 300;
                };
                if (prevWordForIntro && nextWordForIntro) {
                    if (
                        shouldCreateInterestPoint(
                            prevWordForIntro,
                            nextWordForIntro,
                            asset.introAnimationOffset,
                        )
                    ) {
                        const prevWordIdForIntro = prevWordForIntro.id;
                        const timeOffsetForIntro = introTimeTranscript - prevWordForIntro.endTime;

                        interestPoints.push({
                            timeType: InterestPointTimeType.AssetIntro,
                            sceneId,
                            assetId,
                            prevWordId: prevWordIdForIntro,
                            timeOffset: timeOffsetForIntro,
                        });
                    }
                }

                const prevWordForOutro = $getPreviousWordAtTime(outroTimeTranscript);
                const nextWordForOutro = $getNextWordAtTime(outroTimeTranscript);

                if (prevWordForOutro && nextWordForOutro) {
                    if (
                        shouldCreateInterestPoint(
                            prevWordForOutro,
                            nextWordForOutro,
                            asset.outroAnimationOffset,
                        )
                    ) {
                        const prevWordIdForOutro = prevWordForOutro.id;
                        const timeOffsetForOutro = outroTimeTranscript - prevWordForOutro.endTime;

                        interestPoints.push({
                            timeType: InterestPointTimeType.AssetOutro,
                            sceneId,
                            assetId,
                            prevWordId: prevWordIdForOutro,
                            timeOffset: timeOffsetForOutro,
                        });
                    }
                }
            }

            return interestPoints;
        });
    },
);

/**
 * Reapplies interest points after a transformation (e.g., overlay translation).
 * Finds the word nodes by ID and recalculates asset times based on the new word positions.
 *
 * If you are inside of an editor.update() block, it's better to use $reapplyInterestPoints directly
 * because it will get the most up-to-date editor state.
 */
export const reapplyInterestPointsOperation = createLuminaryOperation(
    (
        { luminary, getState, call },
        interestPoints: InterestPointTime[],
        sceneGroupId: string,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const editor = getEditorFromSceneGroupId(getState(), sceneGroupId);
        if (!editor) {
            return;
        }

        return editor.getEditorState().read(() => {
            call(
                $reapplyInterestPoints,
                interestPoints,
                sceneGroupId,
                systemTriggered,
                parentActionGroup,
            );
        });
    },
);

export const $reapplyInterestPoints = createLuminaryOperation(
    (
        { luminary, call },
        interestPoints: InterestPointTime[],
        sceneGroupId: string,
        systemTriggered: boolean,
        parentActionGroup: string,
    ) => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return;
        }

        const voiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
        const voiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);

        luminary.batchRun(() => {
            for (const interestPoint of interestPoints) {
                const scene = luminary.getSceneById(interestPoint.sceneId);
                if (!scene) {
                    continue;
                }

                const asset = scene.getAssetById(interestPoint.assetId);
                if (!asset) {
                    continue;
                }

                let newTimeTranscript: number;
                if (interestPoint.prevWordId === null) {
                    // Before the first word, so the intereset point is relative to the start of the transcript
                    newTimeTranscript = interestPoint.timeOffset;
                } else {
                    const prevWord = $getWordNodeByWordId(interestPoint.prevWordId);
                    if (!prevWord) {
                        // Word not found - it might have been deleted or something. Skip this interest point.
                        continue;
                    }
                    newTimeTranscript = prevWord.endTime + interestPoint.timeOffset;
                }

                const newTimeVideo = translateTranscriptTimeToVideoTime(
                    newTimeTranscript,
                    voiceDrivenAssetPlaybackRegions,
                    voiceDrivenAssetSourceDuration,
                    sceneGroup.startTime,
                );

                const newTimeSceneRelative = Math.min(
                    Math.max(0, newTimeVideo - scene.startTime),
                    scene.duration,
                );

                const currentIntroTime = asset.introAnimationOffset;
                const currentExitTime = scene.duration - asset.outroAnimationOffset;

                // Apply the new time based on timeType
                switch (interestPoint.timeType) {
                    case InterestPointTimeType.AssetIntro:
                        call(setAssetIntroExitTimesOperation, {
                            sceneId: interestPoint.sceneId,
                            assetId: interestPoint.assetId,
                            introTime: newTimeSceneRelative,
                            exitTime: currentExitTime,
                            systemTriggered,
                            parentActionGroup,
                        });
                        break;
                    case InterestPointTimeType.AssetOutro:
                        call(setAssetIntroExitTimesOperation, {
                            sceneId: interestPoint.sceneId,
                            assetId: interestPoint.assetId,
                            introTime: currentIntroTime,
                            exitTime: newTimeSceneRelative,
                            systemTriggered,
                            parentActionGroup,
                        });
                        break;
                }
            }
        });
    },
);
